import {
  createConnectQueryKey,
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from "@connectrpc/connect-query";

import {
  GetProjectRequest,
  GetProjectResponse,
  ListProjectsCountRequest,
  ListProjectsCountResponse,
  ListProjectsRequest,
  ListProjectsRequestSchema,
  ListProjectsResponse,
} from "@gx/ts-contracts/services/ontology/api/project_pb";
import {
  getProject,
  listProjects,
  listProjectsCount,
} from "@gx/ts-contracts/services/ontology/api/project-ProjectService_connectquery";
import { PaginationRequestSchema } from "@gx/ts-contracts/shared/pagination_pb";
import { SortingRequestSchema } from "@gx/ts-contracts/shared/sorting_pb";

import {
  DEFAULT_PAGINATION_CURSOR,
  DEFAULT_PAGINATION_TOTAL,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
} from "@/api/constants";
import { createInfiniteQueryInput, UseInfiniteQueryOptions } from "@/api/types";

// ========== GET PROJECT ==========

export const createGetProjectQueryKey = (input: GetProjectRequest) => {
  return createConnectQueryKey({
    schema: getProject,
    input,
    cardinality: "finite",
  });
};

export const useGetProjectQuery = ({
  input,
  options = {},
}: {
  input: GetProjectRequest;
  options?: UseQueryOptions<typeof getProject.output, GetProjectResponse>;
}) => {
  return useQuery<typeof getProject.input, typeof getProject.output>(getProject, input, options);
};

// ========== LIST PROJECTS ==========

export const createListProjectsQueryKey = (input?: ListProjectsRequest) => {
  return createConnectQueryKey({
    schema: listProjects,
    input,
    cardinality: "finite",
  });
};

export const useListProjectsQuery = ({
  input,
  options = {},
}: {
  input?: ListProjectsRequest;
  options?: UseQueryOptions<typeof listProjects.output, ListProjectsResponse>;
}) => {
  return useQuery<typeof listProjects.input, typeof listProjects.output>(
    listProjects,
    input,
    options,
  );
};

// ========== LIST PROJECTS COUNT ==========

export const createListProjectsCountQueryKey = (input?: ListProjectsCountRequest) => {
  return createConnectQueryKey({
    schema: listProjectsCount,
    input,
    cardinality: "finite",
  });
};

export const useListProjectsCountQuery = ({
  input,
  options = {},
}: {
  input?: ListProjectsCountRequest;
  options?: UseQueryOptions<typeof listProjectsCount.output, ListProjectsCountResponse>;
}) => {
  return useQuery<typeof listProjectsCount.input, typeof listProjectsCount.output>(
    listProjectsCount,
    input,
    options,
  );
};

// ========== LIST PROJECTS INFINITE ==========

export const createListProjectsInfiniteQueryKey = (input?: ListProjectsRequest) => {
  return createConnectQueryKey({
    schema: listProjects,
    input,
    cardinality: "infinite",
  });
};

export const useListProjectsInfiniteQuery = ({
  input,
  options = {},
}: {
  input?: ListProjectsRequest;
  options?: UseInfiniteQueryOptions<
    typeof listProjects.input,
    typeof listProjects.output,
    "pagination"
  >;
}) => {
  const inputTotal = input?.pagination?.total ?? DEFAULT_PAGINATION_TOTAL;
  const inputCursor = input?.pagination?.cursor ?? DEFAULT_PAGINATION_CURSOR;
  const inputSortBy = input?.sorting?.sortBy ?? DEFAULT_SORT_BY;
  const inputSortOrder = input?.sorting?.sortOrder ?? DEFAULT_SORT_ORDER;
  const inputQuery = input?.query;

  return useInfiniteQuery<typeof listProjects.input, typeof listProjects.output, "pagination">(
    listProjects,
    createInfiniteQueryInput<typeof ListProjectsRequestSchema, "pagination">(
      ListProjectsRequestSchema,
      {
        pagination: createInfiniteQueryInput(PaginationRequestSchema, {
          total: inputTotal,
          cursor: inputCursor,
        }),
        sorting: createInfiniteQueryInput(SortingRequestSchema, {
          sortBy: inputSortBy,
          sortOrder: inputSortOrder,
        }),
        ...(inputQuery !== undefined ? { query: inputQuery } : {}),
      },
    ),
    {
      pageParamKey: "pagination",
      getNextPageParam: (lastPage) => {
        if (!lastPage.pagination?.nextCursor) return undefined;
        return createInfiniteQueryInput(PaginationRequestSchema, {
          total: inputTotal,
          cursor: lastPage.pagination.nextCursor,
        });
      },
      ...options,
    },
  );
};
